import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { delay } from 'redux-saga';
import { get } from 'lodash';
import {
  actions as macheteActions,
  getModel,
  selectors,
} from 'src/machete-redux';
import trackError from 'src/lib/rollbar';
import request from 'src/lib/request';
import { configUtils } from 'src/config';
import appDux from 'src/redux/app';
import { setNodeStylesSaga } from 'src/redux/app/sagas';
import notificationDux from 'src/redux/notification';
import constants from 'src/constants';
import jobDesignsDux from 'src/redux/jobDesigns';
import { loadModelSaga } from 'src/redux/machete/sagas';
import ViewerApi from 'src/apis/viewer';
import utils from 'src/utils';
import env from 'src/env';
import { shouldSaveDesignHDF } from 'src/featureFlags';
import '@hover/machete-core';
import Exporter from '@hover/machete-exporter';
import machete from '../machete';

const { buildQuery } = utils.api;
const { isValidSavedDesign } = utils.jobDesign;
const { SHARE_TOKEN_PARAM } = constants.jobShare;
const { ORIGINAL_DESIGN_ID } = constants.jobDesign;
const {
  CREATE_DESIGN_FAILED,
  UPDATE_DESIGN_FAILED,
  UPDATE_DESIGN_HDF_FAILED,
  GET_UPLOAD_URLS_FAILED,
  SAVE_DESIGN_FAILED,
} = constants.errors;
const { SAVE_NOTIFICATION } = constants.notification;
const { isRoofOnly: isRoofOnlyProperty } = utils.property;

export function* generateModelOpts({ geometrySrc, metadataSrc }) {
  const shareToken = yield select(appDux.selectors.getShareToken);

  const opts = { withCredentials: true };
  if (shareToken) opts.query = buildQuery(SHARE_TOKEN_PARAM, shareToken);

  // always refetch to avoid machete mutating the data
  const [{ data: geometry }, { data: metadata }] = yield all([
    call([request, request.get], geometrySrc, opts),
    call([request, request.get], metadataSrc, opts),
  ]);

  return {
    autotexture: false,
    autofocus: false,
    geometry,
    metadata,
  };
}

export function* fetchDesignDetails({ jobId, designId }) {
  const { data: designDetails } = yield call(
    [ViewerApi, ViewerApi.getJobDesignDetails],
    {
      jobId,
      designId,
    },
  );

  return {
    id: designId,
    metadataSrc: designDetails.machete_json.url,
    geometrySrc: designDetails.model_json_v3.url,
    filesUploadedUrl: `${env.API_SERVER}/api/v2/jobs/${jobId}/styles/${designId}/files_uploaded`,
  };
}

const convertToApiUrl = (url) =>
  url && url.replace(window.location.origin, env.API_SERVER);

export function* generateJobDesignsSaga({ jobId, model, savedDesigns }) {
  const jobDesigns = {
    original: {
      id: ORIGINAL_DESIGN_ID,
      // The url base needs to be the API server, or it will fail in
      // e2e tests. The suggested fix would be to update the backend api
      // to return the correct url, but it's a little risky and not worth
      // changing it for just design pro.
      geometrySrc: convertToApiUrl(model.model_json_v3?.url),
      metadataSrc: convertToApiUrl(model.machete?.url),
    },
    saved: [],
  };

  if (savedDesigns.length) {
    jobDesigns.saved[0] = yield call(fetchDesignDetails, {
      jobId,
      designId: savedDesigns[0].id,
    });
  }

  let selectedModelData;
  const savedData = jobDesigns.saved[0];

  if (isValidSavedDesign(savedData)) {
    selectedModelData = savedData;
  } else {
    selectedModelData = jobDesigns.original;
    jobDesigns.saved = [];
  }

  selectedModelData.opts = yield call(generateModelOpts, selectedModelData);

  return jobDesigns;
}

function* selectDesignSaga(action) {
  const { selectedDesignId } = action.payload;
  const jobDesigns = yield select(jobDesignsDux.selectors.getJobDesigns);
  const { id: jobId } = yield select(appDux.selectors.getJobDetails);
  const designData =
    selectedDesignId === ORIGINAL_DESIGN_ID
      ? jobDesigns.original
      : jobDesigns.saved.find(({ id }) => id === selectedDesignId);
  const shouldFetchDesignDetails = !designData.geometrySrc;

  yield put(jobDesignsDux.actions.updateIsSwitchingDesign(true));
  yield put(jobDesignsDux.actions.updateSelectedDesignId(selectedDesignId));

  // short delay to allow spinner to start spinning
  yield call(delay, 100);

  if (shouldFetchDesignDetails) {
    const data = yield call(fetchDesignDetails, {
      jobId,
      designId: designData.id,
    });

    designData.geometrySrc = data.geometrySrc;
    designData.metadataSrc = data.metadataSrc;
  }

  designData.opts = yield call(generateModelOpts, designData);
  yield put(jobDesignsDux.actions.receiveJobDesigns(jobDesigns));

  yield put(macheteActions.segmentation.clearNodeSelection());
  yield call(loadModelSaga, { modelData: designData });

  yield call(setNodeStylesSaga);
  yield put(appDux.actions.setDesignProInitComplete());
  yield put(jobDesignsDux.actions.updateIsSwitchingDesign(false));
}

export function* updateDesignSaga(jobDesigns, jobId) {
  const designData = jobDesigns.saved[0];
  const { filesUploadedUrl, metadataSrc, geometrySrc } = designData;
  const modelJson = getModel().exportAsModelJSONv3();
  const macheteJson = getModel().exportAsMacheteJSON();
  const jobDetails = yield select(appDux.selectors.getJobDetails);

  try {
    // upload data to S3, then let hyperion know we updated it
    yield all([
      call(ViewerApi.uploadToS3, geometrySrc, modelJson),
      call(ViewerApi.uploadToS3, metadataSrc, macheteJson),
    ]);

    if (shouldSaveDesignHDF()) {
      let computeMeasurements = true;
      const deliverables = [];
      const isRoofOnly = isRoofOnlyProperty(jobDetails.deliverable_id);
      if (isRoofOnly) {
        deliverables.push('ROOF');
      } else {
        deliverables.push('COMPLETE');
      }
      for (let i = 0; i < 2; i++) {
        try {
          // Create/update the style's HDF. We make a separate yield...call because
          // we don't want a failure here to interrupt the save operation if the other
          // requests succeed.
          const hdf = Exporter.HDF.fromBuilding(getModel(), {
            computeMeasurements,
            locale: 'en-US',
            deliverables,
          });
          const hdfStr = JSON.stringify(hdf);
          yield call(ViewerApi.createDesignHDF, {
            jobId,
            designId: designData.id,
            hdfStr,
          });

          break;
        } catch (e) {
          computeMeasurements = false;
          // Just log the error, don't throw any errors.
          trackError(UPDATE_DESIGN_HDF_FAILED, e);
        }
      }
    }

    yield call(ViewerApi.updateFilesLoaded, filesUploadedUrl);

    designData.opts = yield call(generateModelOpts, designData);

    yield put(
      jobDesignsDux.actions.receiveJobDesigns({
        original: jobDesigns.original,
        saved: [designData],
      }),
    );
  } catch (e) {
    trackError(UPDATE_DESIGN_FAILED, e);
    throw e;
  }
}

export function* createDesignSaga(jobId, modelId) {
  try {
    const {
      data: { id, machete_json_upload_url, model_json_v3_upload_url },
    } = yield call(ViewerApi.createDesign, {
      jobId,
      name: `Model #${modelId}`,
    });

    return {
      id,
      metadataSrc: machete_json_upload_url,
      geometrySrc: model_json_v3_upload_url,
      filesUploadedUrl: `${env.API_SERVER}/api/v2/jobs/${jobId}/styles/${id}/files_uploaded`,
    };
  } catch (e) {
    trackError(CREATE_DESIGN_FAILED, e);
    throw e;
  }
}

export function* getUploadUrls({ jobId, modelId, designId }) {
  try {
    const {
      data: { id, machete_json_upload_url, model_json_v3_upload_url },
    } = yield call(ViewerApi.updateSavedDesign, {
      jobId,
      designId,
      name: `Model #${modelId}`,
    });

    return {
      id,
      metadataSrc: machete_json_upload_url,
      geometrySrc: model_json_v3_upload_url,
      filesUploadedUrl: `${env.API_SERVER}/api/v2/jobs/${jobId}/styles/${id}/files_uploaded`,
    };
  } catch (e) {
    trackError(GET_UPLOAD_URLS_FAILED, e);
    throw e;
  }
}

export function* saveDesignSaga() {
  const jobDesigns = yield select(jobDesignsDux.selectors.getJobDesigns);
  const { id: jobId } = yield select(appDux.selectors.getJobDetails);
  const designId = get(jobDesigns, 'saved[0].id');

  try {
    yield put(jobDesignsDux.actions.updateIsSavingDesign(true));
    const model = yield select(machete.selectors.getHyperionModelData);
    const modelId = model.id;

    // if design exists refetch to refresh request expirations, otherwise, create it
    jobDesigns.saved[0] = designId
      ? yield call(getUploadUrls, { jobId, modelId, designId })
      : yield call(createDesignSaga, jobId, modelId);

    yield call(updateDesignSaga, jobDesigns, jobId);

    yield put(notificationDux.actions.handleNotification(SAVE_NOTIFICATION));
    yield put(
      jobDesignsDux.actions.updateSelectedDesignId(jobDesigns.saved[0].id),
    );
  } catch (e) {
    trackError(SAVE_DESIGN_FAILED, e);
  } finally {
    yield put(jobDesignsDux.actions.updateIsSavingDesign(false));
  }
}

export default function* sagas() {
  yield all([
    takeLatest(jobDesignsDux.actions.selectDesign, selectDesignSaga),
    takeLatest(jobDesignsDux.actions.saveDesign, saveDesignSaga),
  ]);
}
